import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { ProblemDetails } from "@ripsim/services";
import { Subject, takeUntil } from "rxjs";
import { AppActions } from "src/app/ngrx/app.actions.types";
import { AppState } from "src/app/ngrx/root.reducer";
import { RIPSIM_ICONS as ICONS } from "src/app/shared/models/icon-config.model";
import { RsIconComponent } from "../../icons/icon.component";
import { GeneralDialogComponent } from "../general-dialog/general-dialog.component";

@Component({
  selector: "rs-http-error-dialog",
  standalone: true,
  imports: [CommonModule, GeneralDialogComponent, RsIconComponent],
  templateUrl: "./http-error-dialog.component.html",
  styleUrl: "./http-error-dialog.component.scss"
})
export class HttpErrorDialogComponent implements OnInit, OnDestroy {
  visible = false;
  message = "";

  private readonly destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>) {}

  readonly ICONS = ICONS;
  readonly defaultTitle = $localize`:@@httpErrorTitle:Service Error`;
  readonly defaultDescription = $localize`:@@httpErrorDefaultDescription:Encountered an unexpected error communicating with the remote service. Please try the operation again and if the problem persists contact support for technical assistance.`;
  readonly timeoutDescription = $localize`:@@httpErrorTimeoutDescription:The request took longer than expected. Please try again in a moment.`;
  readonly unauthorizedDescription = $localize`:@@httpErrorUnauthorizedDescription:Your session may no longer be valid for this request. Refresh the page or sign in again, then try again.`;
  readonly forbiddenDescription = $localize`:@@httpErrorForbiddenDescription:You do not have permission to complete this action. Contact an administrator if you believe this is unexpected.`;
  readonly notFoundDescription = $localize`:@@httpErrorNotFoundDescription:The requested information could not be found. Refresh the page and try again.`;
  readonly serviceUnavailableDescription = $localize`:@@httpErrorServiceUnavailableDescription:The service is temporarily unavailable. Please try again in a moment.`;
  readonly dialogStyle = {
    width: "min(92vw, 48rem)"
  };

  ngOnInit(): void {
    this.store
      .select("app")
      .pipe(takeUntil(this.destroy$))
      .subscribe((appState) => {
        this.visible = appState.showHttpError;
        this.message = this.buildMessage(appState.httpErrorDescription, appState.httpErrorDetails);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleClose(): void {
    this.store.dispatch(AppActions.HideHttpError());
  }

  private buildMessage(description: string, details: unknown): string {
    const detailRecord = this.toProblemDetailsRecord(details);
    const candidateMessages = [
      this.readString(detailRecord, "detail"),
      this.readString(detailRecord, "message"),
      this.readString(detailRecord, "error_description"),
      this.readString(detailRecord, "errorDescription"),
      this.readString(detailRecord, "title"),
      typeof details === "string" && !this.looksLikeJson(details) ? details : null
    ];

    for (const candidateMessage of candidateMessages) {
      if (candidateMessage) {
        const sanitizedMessage = this.sanitizeMessage(candidateMessage);
        if (sanitizedMessage) {
          return sanitizedMessage;
        }
      }
    }

    return this.friendlyFallback(description);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  private toProblemDetailsRecord(details: unknown): ProblemDetails | null {
    if (this.isProblemDetails(details)) {
      return details;
    }

    if (typeof details !== "string") {
      return null;
    }

    const parsedDetails = this.tryParseJson(details);
    return this.isProblemDetails(parsedDetails) ? parsedDetails : null;
  }

  private isProblemDetails(value: unknown): value is ProblemDetails {
    if (!this.isRecord(value)) {
      return false;
    }

    return ["detail", "title", "status", "type", "instance"].some((key) => key in value);
  }

  private readString(record: Record<string, unknown> | null, key: string): string | null {
    if (!record) {
      return null;
    }
    const value = record[key];
    return typeof value === "string" ? value : null;
  }

  private tryParseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private looksLikeJson(value: string): boolean {
    const trimmedValue = value.trim();
    return trimmedValue.startsWith("{") || trimmedValue.startsWith("[");
  }

  private sanitizeMessage(value: string): string {
    return value.replace(/\s+/g, " ").trim();
  }

  private friendlyFallback(description: string): string {
    if (/timeout/i.test(description)) {
      return this.timeoutDescription;
    }

    const statusMatch = description.match(/\b(401|403|404|408|500|502|503|504)\b/);
    switch (statusMatch?.[1]) {
      case "401":
        return this.unauthorizedDescription;
      case "403":
        return this.forbiddenDescription;
      case "404":
        return this.notFoundDescription;
      case "408":
      case "504":
        return this.timeoutDescription;
      case "500":
      case "502":
      case "503":
        return this.serviceUnavailableDescription;
      default:
        return this.defaultDescription;
    }
  }
}
